import type { PrioritySchemeValidationError, RequestMappings } from '../../common/ui/types';
import type {
	RefinedFetchError,
	SavePrioritySchemeSuccessResponse,
	SaveSchemeResult,
} from '../types';
import {
	refineValidationError,
	fetchWithStatus,
	refineFetchError,
	refineCreatePrioritySchemeResponse,
} from '../utils';

export type CreatePrioritySchemeRequest = {
	/**
	 * User generated content
	 */
	name: string;
	/**
	 * User generated content
	 */
	description: string;
	/**
	 * User generated content, each id is safe and unique for a jira instance.
	 */
	priorityIds: string[];
	/**
	 * User generated content, id must exist in priorityIds[]
	 */
	defaultPriorityId: string;
	/**
	 * User generated content, each id is safe and unique for a jira instance.
	 */
	projectIds?: string[];
	/**
	 * User generated content. Mappings will be executed in given order.
	 */
	mappings?: RequestMappings;
};

export const createPrioritySchemeRequest = async (
	requestParams: CreatePrioritySchemeRequest,
): Promise<SaveSchemeResult> => {
	try {
		const URL = '/rest/api/2/priorityscheme';
		const response: unknown = await fetchWithStatus(URL, {
			method: 'POST',
			body: JSON.stringify(requestParams),
		});

		const responseData: SavePrioritySchemeSuccessResponse | undefined =
			refineCreatePrioritySchemeResponse(response);

		if (!responseData) {
			return {
				isSuccess: false,
			};
		}

		if (responseData.status === 202) {
			return {
				isSuccess: true,
				isMigrationPending: true,
				data: responseData.body,
			};
		}

		return {
			isSuccess: true,
			isMigrationPending: false,
			data: responseData.body,
		};
	} catch (error: unknown) {
		const fetchError: RefinedFetchError | undefined = await refineFetchError(error);
		if (fetchError) {
			const { statusCode, responseJson } = fetchError;
			const body: unknown = responseJson;

			if (statusCode === 400) {
				const validationErrors: PrioritySchemeValidationError[] | undefined =
					refineValidationError(body);
				if (validationErrors) {
					return {
						isSuccess: false,
						errors: validationErrors,
					};
				}
			}
		}
		throw error;
	}
};
